/**
 * ============================================================================
 * ALERTS MOCK SERVICE — DATOS SIMULADOS
 * ============================================================================
 *
 * TODO(backend): Este servicio devuelve datos MOCK. Todavía no existen los
 * endpoints reales en el backend. Cuando estén disponibles, reemplazar el
 * cuerpo de cada función por la llamada axios correspondiente (usar la
 * instancia de `src/utils/Apis/MCMCore/axios.js`) MANTENIENDO la misma forma
 * de respuesta, para que el AlertsContext y los componentes no cambien.
 *
 * Endpoints propuestos (a confirmar con backend):
 *   GET /api/Alerts/user/{employeeId}            → fetchUserAlerts
 *   GET /api/Alerts/unsigned-notes/{employeeId}  → fetchUnsignedNotes
 *
 * Documentación de contratos: hipaa-logs/Docs/Control/ (cuando exista).
 * ============================================================================
 */

import dayjs from "dayjs";
import { getPendingSignNotes } from "./pendingNotesStore";
import { getOpenPhysicianQueries } from "./physicianQueriesStore";
import { getCareGapReviews } from "./careGapReviewsStore";

/** Categorías de alerta soportadas por el panel. */
export const ALERT_CATEGORIES = {
  CLINICAL_NOTES: "clinical_notes",
  CARE_GAPS: "care_gaps",
  PHYSICIAN_QUERIES: "physician_queries",
  PATIENT_OBSERVATIONS: "patient_observations",
  ASSIGNED_TASKS: "assigned_tasks",
};

/** Latencia simulada para imitar una llamada de red real. */
const simulateNetworkDelay = (ms = 350) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Obtiene las alertas del usuario agrupables por categoría.
 *
 * MOCK: genera un set fijo de alertas con timestamps relativos al momento
 * de la llamada para que el "hace X minutos/horas" se vea vivo.
 *
 * @param {number|string} employeeId - ID del empleado autenticado
 * @returns {Promise<Array<{
 *   alertId: string,
 *   category: string,       // uno de ALERT_CATEGORIES
 *   title: string,
 *   description: string,
 *   createdAt: string,      // ISO 8601
 *   read: boolean
 * }>>}
 *
 * Ejemplo de llamada futura:
 * GET /api/Alerts/user/123
 *
 * Respuesta esperada (200):
 * [
 *   {
 *     "alertId": "cn-1042",
 *     "category": "clinical_notes",
 *     "title": "Pending note: Annual Wellness 2.0",
 *     "description": "Note for patient J. Smith has been pending signature for 2 days.",
 *     "createdAt": "2026-08-04T14:30:00Z",
 *     "read": false
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found" }
 */
export const fetchUserAlerts = async (employeeId) => {
  await simulateNetworkDelay();

  const now = dayjs();

  // --- Pending clinical notes: REALES, del store local -----------------------
  // Las notas terminadas en CDCI (pendientes de firma en AdvancedMD) salen de
  // pendingNotesStore (localStorage) — acá NO hay mocks: si no cerraste
  // encuentros, la categoría llega vacía.
  const pendingNoteAlerts = getPendingSignNotes().map((n) => ({
    alertId: `cn-cdci-${n.appointmentId}`,
    category: ALERT_CATEGORIES.CLINICAL_NOTES,
    title: `Pending signature: ${n.templateName}`,
    description:
      `The note for ${n.patientName}${n.chartNumber ? ` (${n.chartNumber})` : ""} was completed ` +
      "in CDCI and is pending signature — please sign it in AdvancedMD.",
    createdAt: n.completedAt,
    read: false,
  }));

  // --- Physician queries (Charge Capture): REALES, del store local ----------
  // Indicador UNIFICADO con las notas sin firmar (CONCEPCION §8.5): misma
  // campana, mismo panel — nunca un contador paralelo.
  const physicianQueryAlerts = getOpenPhysicianQueries().map((q) => ({
    alertId: `pq-${q.queryId}`,
    category: ALERT_CATEGORIES.PHYSICIAN_QUERIES,
    title: `Billing query pending: ${q.patientName}`,
    description: `${q.physician} has an open billing query for ${q.patientName} (case ${q.caseId}). Answer it from the physician queries screen.`,
    createdAt: q.openedAt,
    read: false,
  }));

  // --- Care gaps revisados en Pre-Visit Planning ----------------------------
  // El Medical Assistant marcó "Open Care Gaps Review" como Complete y el
  // paciente TIENE care gaps abiertos: quien atienda la visita tiene que
  // saberlo antes de entrar al consultorio.
  const careGapAlerts = getCareGapReviews().map((r) => ({
    alertId: `cg-${r.appointmentId}`,
    category: ALERT_CATEGORIES.CARE_GAPS,
    title: `Care gaps reviewed: ${r.patientName}`,
    description:
      `Open care gaps were reviewed in Pre-Visit Planning. ${r.patientName}` +
      `${r.chartNumber ? ` (${r.chartNumber})` : ""} has ${r.openCareGaps} open care gap` +
      `${r.openCareGaps === 1 ? "" : "s"}${r.appointmentDate ? ` for the ${r.appointmentDate} visit` : ""}` +
      " — address them during the encounter.",
    createdAt: r.reviewedAt,
    read: false,
  }));

  return [
    ...pendingNoteAlerts,
    ...careGapAlerts,
    ...physicianQueryAlerts,
    // --- Patient observations ---------------------------------------------
    {
      alertId: "obs-2210",
      category: ALERT_CATEGORIES.PATIENT_OBSERVATIONS,
      title: "New observation on watched patient",
      description:
        "Patient R. Johnson (watchlist) was admitted to the ER last night.",
      createdAt: now.subtract(9, "hour").toISOString(),
      read: false,
    },
    {
      alertId: "obs-2214",
      category: ALERT_CATEGORIES.PATIENT_OBSERVATIONS,
      title: "Observation updated",
      description:
        "Care coordinator added a note to patient L. Chen's observation thread.",
      createdAt: now.subtract(1, "day").toISOString(),
      read: true,
    },
    // --- Assigned tasks ----------------------------------------------------
    {
      alertId: "task-3301",
      category: ALERT_CATEGORIES.ASSIGNED_TASKS,
      title: "Task assigned: Verify insurance eligibility",
      description:
        "Verify eligibility for 4 new members before their first appointment.",
      createdAt: now.subtract(30, "minute").toISOString(),
      read: false,
    },
    {
      alertId: "task-3298",
      category: ALERT_CATEGORIES.ASSIGNED_TASKS,
      title: "Task due today: Call-back list",
      description:
        "Complete the pending call-back list for the Fort Myers center.",
      createdAt: now.subtract(3, "hour").toISOString(),
      read: false,
    },
  ];
};

/**
 * Obtiene las notas clínicas sin firmar del usuario en AdvancedMD.
 * Alimenta el dialog de login ("Unsigned clinical notes in AdvancedMD"),
 * que lista cada nota con los días que lleva sin firmarse.
 *
 * MOCK: devuelve 3 notas con fechas relativas al momento de la llamada para
 * que los "days unsigned" se calculen de verdad en la UI.
 *
 * @param {number|string} employeeId - ID del empleado autenticado
 * @returns {Promise<{
 *   total: number,
 *   notes: Array<{
 *     noteId: string,
 *     noteType: string,       // template de la nota en AdvancedMD
 *     patientName: string,
 *     chartNumber: string,    // MRN del paciente
 *     serviceDate: string     // ISO 8601 — fecha del encuentro/creación de la nota
 *   }>
 * }>}
 *
 * Ejemplo de llamada futura:
 * GET /api/Alerts/unsigned-notes/123
 *
 * Respuesta esperada (200):
 * {
 *   "total": 3,
 *   "notes": [
 *     {
 *       "noteId": "amd-88121",
 *       "noteType": "Annual Wellness 2.0",
 *       "patientName": "John Smith",
 *       "chartNumber": "MCM10231",
 *       "serviceDate": "2026-07-25T09:30:00Z"
 *     }
 *   ]
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Employee not found" }
 */
export const fetchUnsignedNotes = async (employeeId) => {
  await simulateNetworkDelay(200);

  // Sin mocks: las notas sin firmar son las TERMINADAS en CDCI que viven en el
  // store local (pendingNotesStore) hasta que exista la inserción productiva en
  // AdvancedMD + el endpoint real de unsigned-notes.
  const notes = getPendingSignNotes().map((n) => ({
    noteId: `cdci-${n.appointmentId}`,
    noteType: n.templateName,
    patientName: n.patientName,
    chartNumber: n.chartNumber || "—",
    serviceDate: n.serviceDate,
  }));

  return { total: notes.length, notes };
};
